import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

export interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delayDuration?: number;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  sideOffset?: number;
}

export const TooltipProvider = TooltipPrimitive.Provider;

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  side = 'top',
  align = 'center',
  delayDuration = 400,
  disabled = false,
  className = '',
  contentClassName = '',
  sideOffset = 4,
}) => {
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild className={className}>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={`
            z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground 
            shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out 
            data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 
            data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 
            data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2
            bg-gray-900 text-white border-gray-700 max-w-xs
            ${contentClassName}
          `.trim()}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-gray-900" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

// Convenience component for simple text tooltips
export const SimpleTooltip: React.FC<{
  children: React.ReactNode;
  text: string;
  side?: TooltipProps['side'];
  className?: string;
}> = ({ children, text, side = 'top', className = '' }) => (
  <Tooltip content={text} side={side} className={className}>
    {children}
  </Tooltip>
);
